import React, { useState, useEffect } from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import SectionWrapper from '../SectionWrapper';

/**
 * OVERVIEW ESSENCE COMPONENT - V7 (REMASTERED)
 * Concept: "Technical Datasheet".
 * Focus: Clarity, Alignment, Information Density without clutter.
 */
const OverviewEssence = () => {
    const [complianceCount, setComplianceCount] = useState(0);

    // Continuous Telemetry Animation
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    // Loop animation logic
                    const animate = () => {
                        let start = 0;
                        const end = 100;
                        const duration = 2000;
                        const increment = end / (duration / 16);

                        const timer = setInterval(() => {
                            start += increment;
                            if (start >= end) {
                                setComplianceCount(end);
                                clearInterval(timer);
                                // Restart after delay
                                setTimeout(animate, 5000);
                            } else {
                                setComplianceCount(Math.ceil(start));
                            }
                        }, 16);
                    };

                    animate();
                    observer.disconnect(); // Trigger once when seen, then loop internally
                }
            },
            { threshold: 0.3 }
        );

        const section = document.getElementById('essencia-genesis');
        if (section) observer.observe(section);

        return () => observer.disconnect();
    }, []);

    return (
        <SectionWrapper
            id="essencia-genesis"
            className="section-essence"
            sx={{
                bgcolor: '#FFFFFF',
                color: '#000000',
                borderBottom: '1px solid #E5E5E5',
            }}
        >
            <Container maxWidth={false} sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 0, md: 0 }, height: '100%' }}> {/* Full bleed inside container container */}
                <Grid container spacing={0} sx={{ height: '100%' }}>

                    {/* LEFT COLUMN: MANIFESTO (THE BLUEPRINT) */}
                    <Grid item xs={12} md={6} sx={{
                        borderRight: { md: '1px solid #E5E5E5' },
                        p: { xs: 4, md: 8, lg: 10 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}>
                        <Typography variant="overline" sx={{ fontFamily: '"JetBrains Mono", monospace', color: '#666', letterSpacing: '0.2em', fontWeight: 600, mb: 4, display: 'flex', alignItems: 'center' }}>
                            02_ESSENCE_LAYER
                        </Typography>

                        <Typography variant="h2" sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: '3rem', md: '4.5rem' },
                            fontWeight: 800,
                            letterSpacing: '-0.04em',
                            lineHeight: 0.95,
                            mb: 4,
                            color: '#000'
                        }}>
                            ARQUITETURA<br />
                            PARA SISTEMAS DE<br />
                            <Box component="span" sx={{ color: '#CC0000' }}>MISSÃO CRÍTICA.</Box>
                        </Typography>

                        <Box sx={{ borderLeft: '2px solid #CC0000', pl: 3, mb: 6 }}>
                            <Typography variant="body1" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1rem', lineHeight: 1.6, color: '#333' }}>
                                O mundo digital não tolera amadorismo. <br />
                                Diretriz: <b>[RESULTADO_VERIFICÁVEL]</b>.
                            </Typography>
                        </Box>

                        {/* METRICS GRID */}
                        <Grid container spacing={0} sx={{ borderTop: '1px solid #E5E5E5' }}>
                            <Grid item xs={4} sx={{ pt: 4, borderRight: '1px solid #E5E5E5', pr: 2 }}>
                                <Typography variant="h3" sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 800, fontSize: '3rem', mb: 0.5, color: '#000' }}>ROI</Typography>
                                <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', textTransform: 'uppercase', color: '#666' }}>DRIVEN_ENG</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ pt: 4, borderRight: '1px solid #E5E5E5', px: 2, textAlign: 'center' }}>
                                <Typography variant="h3" sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 800, fontSize: '3rem', mb: 0.5, color: '#000' }}>SECURE</Typography>
                                <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', textTransform: 'uppercase', color: '#666' }}>BY_DESIGN</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ pt: 4, pl: 2, textAlign: 'right' }}>
                                <Typography variant="h3" sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 800, fontSize: '3rem', mb: 0.5, color: '#000' }}>{complianceCount}%</Typography>
                                <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', textTransform: 'uppercase', color: '#666' }}>COMPLIANT</Typography>
                            </Grid>

                        </Grid>
                    </Grid>

                    {/* RIGHT COLUMN: THE CLEAN ROOM VIEWPORT */}
                    <Grid item xs={12} md={6} sx={{ position: 'relative', height: { xs: '500px', md: '100%' } }}>
                        <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: '#F5F5F7', // Lab Table Gray
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                        }}>
                            {/* The Image is a "Slide" or "Specimen" */}
                            <Box
                                component="img"
                                src="/images/office-essence-client.png"
                                alt="R5 Clean Room"
                                sx={{
                                    width: '80%',
                                    height: 'auto',
                                    maxWidth: '600px',
                                    filter: 'grayscale(100%) contrast(110%)', // Technical B&W
                                    transition: 'all 0.5s ease',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                                    border: '1px solid #000', // Hard border
                                    '&:hover': {
                                        filter: 'grayscale(0%) contrast(100%)', // Reveal Color (Life)
                                        transform: 'scale(1.02)'
                                    }
                                }}
                            />

                            {/* Technical Overlays */}
                            <Box sx={{ position: 'absolute', top: 40, right: 40, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', color: '#999', writingMode: 'vertical-rl' }}>
                                FIG. 02-A // TEAM_SYNCHRONIZATION
                            </Box>
                        </Box>
                    </Grid>

                </Grid>
            </Container>
        </SectionWrapper>
    );
};

export default OverviewEssence;
